import React from 'react';
import { useUserRoles, UserRole } from '../hooks/useUserRoles';

interface AppRoleGuardProps {
  children: React.ReactNode;
  requiredPermission?: {
    module: string;
    action: string;
  };
  requiredRole?: UserRole;
  fallback?: React.ReactNode;
  requireAdmin?: boolean;
  requireManager?: boolean;
}

export const AppRoleGuard: React.FC<AppRoleGuardProps> = ({
  children,
  requiredPermission,
  requiredRole,
  fallback = null,
  requireAdmin = false,
  requireManager = false
}) => {
  const { hasPermission, hasRole, isAdmin, isManager, loading } = useUserRoles();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <>{fallback}</>;
  }

  // Check manager requirement
  if (requireManager && !isManager && !isAdmin) {
    return <>{fallback}</>;
  }

  // Check specific role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return <>{fallback}</>;
  }

  // Check permission requirement
  if (requiredPermission && !hasPermission(requiredPermission.module, requiredPermission.action)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Utility component for unauthorized access
export const UnauthorizedMessage: React.FC<{ message?: string }> = ({ 
  message = "Vous n'avez pas les permissions nécessaires pour accéder à cette section." 
}) => (
  <div className="flex flex-col items-center justify-center p-8 text-center">
    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Accès non autorisé</h3>
    <p className="text-gray-600 max-w-md">{message}</p>
  </div>
);
